/**
 * Parser: Converts DiagramData JSON → React Flow format
 */

import { Node, Edge } from '@xyflow/react'
import type {
  DiagramData,
  DiagramNode,
  DiagramEdge,
  Container,
  Position,
} from '../types/schema'
import { assignEdgeOffsets, type NodePosition } from './edge-offset'
import { autoAssignHandles } from './handle-assignment'
import { assignStaggeredLabelPositions } from './labelPlacement'
import { deriveForeignKeyEdges } from './foreignKeyEdges'
import { STRUCTURED_CONTENT_KEYS } from '../types/schema'

/** Pass every structured-content field (SSOT) from a DiagramNode into React Flow data. */
function passStructuredContent(node: any): Record<string, any> {
  const out: Record<string, any> = {}
  for (const key of STRUCTURED_CONTENT_KEYS) out[key] = node?.[key]
  return out
}

export interface ParseResult {
  nodes: Node[]
  edges: Edge[]
  containers: Container[]
}

/**
 * Calculate automatic bounds for containers/shapes based on their children
 * Uses metadata.for to determine which nodes are children
 */
function calculateElementBounds(
  element: Container | DiagramNode,
  allNodes: DiagramNode[]
): { position: Position; size: { width: number; height: number } } | null {
  // Check if metadata.for exists
  const childIds = element.metadata?.for
  if (!childIds || !Array.isArray(childIds) || childIds.length === 0) {
    return null // No auto-bounds needed
  }

  // Get child nodes
  const children = allNodes.filter(node => childIds.includes(node.id))
  if (children.length === 0) {
    return null // No valid children found
  }

  // Calculate bounding box from child positions
  // Account for node dimensions (default 120x120)
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity

  children.forEach(child => {
    const x = child.position?.x || 0
    const y = child.position?.y || 0
    const width = child.size?.width || 120
    const height = child.size?.height || 120

    // Calculate bounds accounting for node size (center point)
    minX = Math.min(minX, x - width / 2)
    maxX = Math.max(maxX, x + width / 2)
    minY = Math.min(minY, y - height / 2)
    maxY = Math.max(maxY, y + height / 2)
  })

  // Add generous padding
  const TOP_PADDING = 80    // Extra space for container header
  const SIDE_PADDING = 50   // Left/Right padding
  const BOTTOM_PADDING = 50 // Bottom padding

  const containerX = minX - SIDE_PADDING
  const containerY = minY - TOP_PADDING
  const containerWidth = (maxX - minX) + (SIDE_PADDING * 2)
  const containerHeight = (maxY - minY) + TOP_PADDING + BOTTOM_PADDING

  // Apply minimums
  const MIN_WIDTH = 300
  const MIN_HEIGHT = 250

  return {
    position: {
      x: containerX,
      y: containerY
    },
    size: {
      width: Math.max(containerWidth, MIN_WIDTH),
      height: Math.max(containerHeight, MIN_HEIGHT)
    }
  }
}

/**
 * Parse DiagramData to React Flow format
 * Includes automatic migration of old type names to new ones
 */
export function parseDiagramData(data: DiagramData): ParseResult {
  const nodes: Node[] = []
  const edges: Edge[] = []
  const containers = data.containers || []

  // Parse containers as special background nodes
  // Pass all nodes for auto-bounds calculation
  containers.forEach((container) => {
    nodes.push(parseContainer(container, data.nodes))
  })

  // Parse nodes
  // Pass all nodes for auto-bounds calculation (for shape parents)
  data.nodes.forEach((node) => {
    nodes.push(parseNode(node, data.diagramType, data.nodes))
  })

  // Parse edges with automatic offset assignment for parallel/bidirectional
  // connections, then append column-anchored crow's-foot FK edges derived from
  // any table's foreignKeys. FK edges already carry explicit column handles +
  // fixedConnection, so they bypass the handle/offset/label passes (which would
  // otherwise re-route them) by being concatenated AFTER those passes.
  const explicitEdges = data.edges ?? []
  const nodePositions: NodePosition[] = nodes.reduce((acc: NodePosition[], node) => {
    const position = node.position as Position | undefined
    if (
      position &&
      Number.isFinite(position.x) &&
      Number.isFinite(position.y)
    ) {
      acc.push({ id: node.id, x: position.x, y: position.y })
    }
    return acc
  }, [])

  // First distribute edges across available handles to reduce overlaps
  const edgesWithHandles = autoAssignHandles(explicitEdges, nodePositions)

  // Then assign offsets with handle awareness (separate handles => zero offset)
  const edgesWithOffsets = assignEdgeOffsets(edgesWithHandles, {
    strategy: 'symmetric',  // Center offsets around 0 for balanced appearance
    nodePositions,
  })

  // Stagger default label positions so parallel edges don't stack their labels
  // (only fills in edges that have no explicit labelPosition)
  const edgesWithLabels = assignStaggeredLabelPositions(edgesWithOffsets)

  // Derive FK edges (deduped against explicit edges via data.derivedFromFk).
  const fkEdges = deriveForeignKeyEdges(data.nodes, explicitEdges)

  // Finally parse each edge (explicit first, then derived FK edges)
  ;[...edgesWithLabels, ...fkEdges].forEach((edge, index) => {
    edges.push(parseEdge(edge, index))
  })

  // Migrate old type names to new ones for backward compatibility
  // This ensures old diagrams show new names in properties panel
  migrateNodeTypes(nodes)

  // Convert absolute positions to relative for nodes with parentId
  convertToRelativePositions(nodes)

  return { nodes, edges, containers }
}

/**
 * Migrate old React Flow node type names to new descriptive names
 * 'universal' → 'icon-library'
 * 'container' → 'group-container'
 */
function migrateNodeTypes(nodes: Node[]): void {
  nodes.forEach((node) => {
    if (node.type === 'universal') {
      node.type = 'icon-library'
    } else if (node.type === 'container') {
      node.type = 'group-container'
    }
  })
}

/**
 * Convert absolute positions to relative for nodes with parentId
 * React Flow expects children to have positions relative to their parent
 */
function convertToRelativePositions(nodes: Node[]): void {
  // Create a map for quick parent lookup
  const nodeMap = new Map<string, Node>()
  nodes.forEach(node => nodeMap.set(node.id, node))

  // Process each node that has a parent
  nodes.forEach(node => {
    if (node.parentId) {
      const parent = nodeMap.get(node.parentId)
      if (parent) {
        // Convert from absolute to relative position
        node.position = {
          x: node.position.x - parent.position.x,
          y: node.position.y - parent.position.y,
        }
      }
    }
  })
}

/**
 * Convert Container to React Flow node (as background group)
 */
function parseContainer(container: Container, allNodes: DiagramNode[]): Node {
  // Try to calculate auto-bounds from metadata.for
  const autoBounds = calculateElementBounds(container, allNodes)

  // Use auto-bounds if available, otherwise use manual position/size
  const position = autoBounds?.position || container.position || { x: 0, y: 0 }
  const width = autoBounds?.size.width || container.size?.width || 400
  const height = autoBounds?.size.height || container.size?.height || 300

  return {
    id: container.id,
    type: 'group-container',  // Use new descriptive name
    position,
    data: {
      label: container.label,
      description: container.description,
      icon: container.icon,
      // Migration: Set originalIcon from icon if not present (for restore functionality)
      originalIcon: (container as any).originalIcon || container.icon,
      metadata: container.metadata,
      containerType: container.type,
      provider: container.metadata?.provider, // Cloud provider for cloud-provider type
      layerId: container.layerId, // Layer assignment (draw.io-style layers)
      // Move visual styles to data so GroupContainerNode can access them
      bgColor: container.style?.bgColor || '#f0f0f0',
      textColor: container.style?.textColor || '#000000',
      borderColor: container.style?.borderColor || '#d0d0d0',
      borderStyle: container.style?.borderStyle || 'solid',
      opacity: container.style?.opacity || 0.5,
      isDropTarget: false, // Initial state for drag-to-parent visual feedback
    },
    width,
    height,
    style: {
      zIndex: -10, // Place containers well behind regular nodes
    },
    // React Flow native parent-child support
    expandParent: container.autoResize || false,  // Auto-resize to fit children
    // Support both old (parentContainer) and new (parentId) for nesting
    parentId: container.parentContainer,
    draggable: true,
    selectable: true,
    deletable: true,
  }
}

/**
 * Convert DiagramNode to React Flow node
 */
function parseNode(node: DiagramNode, diagramType: string, allNodes: DiagramNode[]): Node {
  // Determine node type based on data
  let nodeType = 'icon-library'  // Use new descriptive name (was 'universal')

  // Check for structured content nodes FIRST (higher priority)
  let hasStructuredContent = false
  if ((node as any).tableContent) {
    nodeType = 'database-table'
    hasStructuredContent = true
  } else if ((node as any).resourceContent) {
    nodeType = 'api-resource'
    hasStructuredContent = true
  } else if ((node as any).endpointContent) {
    nodeType = 'api-endpoint'
    hasStructuredContent = true
  } else if ((node as any).classContent) {
    nodeType = 'uml-class'
    hasStructuredContent = true
  } else if ((node as any).stateContent) {
    nodeType = 'state-node'
    hasStructuredContent = true
  } else if ((node as any).c4Content) {
    nodeType = 'c4-node'
    hasStructuredContent = true
  } else if ((node as any).sequenceContent) {
    // Sequence nodes keep their explicit size (lifeline height drives the layout),
    // so they are NOT flagged hasStructuredContent (which would null width/height).
    nodeType = 'sequence-lifeline'
  } else if ((node as any).sequenceFragment) {
    nodeType = 'sequence-fragment'
  } else if ((node as any).sequenceNote) {
    nodeType = 'sequence-note'
  } else if ((node as any).ganttContent) {
    nodeType = 'gantt-task'
  } else if ((node as any).ganttSection) {
    nodeType = 'gantt-section'
  } else if ((node as any).ganttAxis) {
    nodeType = 'gantt-axis'
  } else if ((node as any).shapeId || (node as any).isShape) {
    nodeType = 'shape'
  } else if ((node as any).nodeType === 'text') {
    nodeType = 'text'
  }

  // Try to calculate auto-bounds ONLY for shape nodes with metadata.for
  let autoBounds = null
  if (nodeType === 'shape' && node.metadata?.for) {
    autoBounds = calculateElementBounds(node, allNodes)
  }

  // Use auto-bounds if available, otherwise use manual position/size
  const position = autoBounds?.position || node.position || { x: 0, y: 0 }

  // For structured content nodes, DON'T set fixed width/height - let component auto-size
  // For other nodes, use specified size or defaults
  const width = hasStructuredContent ? undefined : (autoBounds?.size.width || node.size?.width || 120)
  const height = hasStructuredContent ? undefined : (autoBounds?.size.height || node.size?.height || 120)

  return {
    id: node.id,
    type: nodeType, // Dynamic type based on node data!
    position,
    data: {
      // Core data
      label: node.label,
      nodeType: node.nodeType,
      subtype: node.subtype,
      diagramType,

      // Visual
      icon: node.icon,
      // Migration: Set originalIcon from icon if not present (for restore functionality)
      originalIcon: (node as any).originalIcon || node.icon,
      description: node.description,

      // Hierarchy
      parentContainer: node.parentContainer,
      layer: node.layer,

      // Layer assignment (draw.io-style layers).
      layerId: node.layerId,

      // Rotation in degrees (applied to node visual content)
      rotation: typeof node.rotation === 'number' ? node.rotation : 0,

      // Shape-specific (for draw.io-style geometric shapes)
      shapeId: node.shapeId,
      shapeCategory: node.shapeCategory,
      isShape: node.isShape,

      // Metadata
      metadata: node.metadata,
      customData: node.customData,

      // Style (keep in data so IconLibraryNode can access it)
      style: node.style,

      // ✅ STRUCTURED CONTENT - pass every content field through from the SINGLE
      // SOURCE OF TRUTH so it survives to the React components (and a new content
      // type is wired automatically — see STRUCTURED_CONTENT_KEYS).
      ...passStructuredContent(node),
    },
    // Set width/height for resizing to work properly
    width,
    height,
    style: {},
    // Stacking order (front/back) — read back onto React Flow's node.zIndex.
    ...(typeof node.zIndex === 'number' ? { zIndex: node.zIndex } : {}),
    // Sequence nodes are authored in TOP-LEFT coordinates (deterministic layout),
    // so override the editor's global center nodeOrigin for them — otherwise each
    // shifts up by half its own height and lifelines/fragments/notes misalign.
    ...(nodeType.startsWith('sequence-') || nodeType.startsWith('gantt-') ? { origin: [0, 0] as [number, number] } : {}),
    // Support React Flow native parent-child (if parentContainer is set)
    parentId: node.parentContainer,  // React Flow's native parent field
    // NO extent - children can move freely (unified logical hierarchy)
    draggable: true,
    selectable: true,
    deletable: true,
  }
}

/**
 * Convert DiagramEdge to React Flow edge
 */
function parseEdge(edge: DiagramEdge, index: number): Edge {
  const zIndexValue = (edge as any).data?.zIndex !== undefined ? (edge as any).data.zIndex : (edge as any).zIndex || 0

  return {
    id: edge.id || `edge-${edge.source}-${edge.target}-${index}`,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    // Default to the full-featured 'custom' edge, but preserve an explicit
    // opt-in 'smart' routing choice so it survives reload/re-import.
    type: edge.type === 'smart' ? 'smart' : 'custom',
    label: edge.label,
    animated: edge.animated || false,
    zIndex: zIndexValue, // Set at edge object level for React Flow rendering order
    style: {}, // Empty style - all visual properties controlled via edge.data
    data: {
      // Visual properties (preserve existing data or use defaults)
      color: edge.style?.stroke || (edge as any).data?.color || '#b1b1b7',
      lineWidth: edge.style?.strokeWidth || (edge as any).data?.lineWidth || 2,
      lineStyle: (edge as any).data?.lineStyle || 'solid',
      lineCap: (edge as any).data?.lineCap || 'butt',
      lineJoin: (edge as any).data?.lineJoin || 'miter',
      opacity: (edge as any).data?.opacity !== undefined ? (edge as any).data.opacity : 1,
      arrowStart: (edge as any).data?.arrowStart || 'none',
      arrowEnd: (edge as any).data?.arrowEnd || 'arrow',
      arrowSize: (edge as any).data?.arrowSize || 1,
      bidirectional: (edge as any).data?.bidirectional || false,
      // Label properties
      labelFontSize: (edge as any).data?.labelFontSize || 12,
      labelBackground: (edge as any).data?.labelBackground || 'white',
      labelBackgroundColor: (edge as any).data?.labelBackgroundColor || '#ffffff',
      labelZIndex: (edge as any).data?.labelZIndex || 1,
      labelPosition: (edge as any).data?.labelPosition ?? 0.5, // Position along path (0.0-1.0)
      labelMode: (edge as any).data?.labelMode || 'default', // Label rendering mode
      labelText: (edge as any).data?.labelText || '', // For split mode or markdown
      // Shadow properties
      shadowEnabled: (edge as any).data?.shadowEnabled || false,
      shadowColor: (edge as any).data?.shadowColor || '#000000',
      shadowBlur: (edge as any).data?.shadowBlur || 3,
      shadowOffsetX: (edge as any).data?.shadowOffsetX || 2,
      shadowOffsetY: (edge as any).data?.shadowOffsetY || 2,
      // Z-index
      zIndex: zIndexValue, // Also store in data for consistency
      // Edge offset and direction (for parallel/bidirectional edge separation)
      offset: (edge as any).data?.offset ?? 0,  // Path offset in pixels
      edgeDirection: (edge as any).data?.edgeDirection || 'single',  // Direction in bidirectional pair
      // Data properties
      edgeType: edge.edgeType,
      protocol: edge.protocol,
      method: edge.method,
      description: edge.description,
      metadata: edge.metadata,
      // Connector routing shape + fixed-vs-floating attachment (draw.io parity).
      pathStyle: (edge as any).data?.pathStyle,
      targetAngle: (edge as any).data?.targetAngle,
      handlePreference: (edge as any).data?.handlePreference,
      fixedConnection: (edge as any).data?.fixedConnection || false,
      // ERD: marks an edge derived from a foreign key (dedupe key + provenance).
      derivedFromFk: (edge as any).data?.derivedFromFk,
      // Layer assignment (draw.io-style layers).
      layerId: edge.layerId,
    },
  }
}

/**
 * Validate DiagramData before parsing
 */
export function validateDiagramData(data: any): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!data) {
    errors.push('Diagram data is null or undefined')
    return { valid: false, errors }
  }

  if (!data.diagramType) {
    errors.push('Missing required field: diagramType')
  }

  if (!data.nodes || !Array.isArray(data.nodes)) {
    errors.push('Missing or invalid field: nodes must be an array')
  } else {
    // Validate each node
    data.nodes.forEach((node: any, index: number) => {
      if (!node.id) {
        errors.push(`Node at index ${index} is missing required field: id`)
      }
      if (!node.label) {
        errors.push(`Node at index ${index} is missing required field: label`)
      }
      if (!node.nodeType) {
        errors.push(`Node at index ${index} is missing required field: nodeType`)
      }
    })
  }

  if (data.edges && !Array.isArray(data.edges)) {
    errors.push('Invalid field: edges must be an array')
  } else if (data.edges) {
    // Validate each edge
    data.edges.forEach((edge: any, index: number) => {
      if (!edge.source) {
        errors.push(`Edge at index ${index} is missing required field: source`)
      }
      if (!edge.target) {
        errors.push(`Edge at index ${index} is missing required field: target`)
      }
    })
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
